import React from 'react';
import classnames from 'classnames/bind';
import { Modal } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAPI } from 'hooks/api/useAPI';
import { useDeleteApplication } from 'hooks/api/useApplications';

import Application from 'types/application';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';

import styles from './DeleteApplicationModal.module.scss';

const c = classnames.bind(styles);

export type DeleteApplicationModalProps = {
  opened: boolean;
  application: Application;
  onClose: () => void;
};

export function DeleteApplicationModal({
  opened,
  application,
  onClose,
}: DeleteApplicationModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { applicationID } = useAPI();

  const { mutate: deleteApplication } = useDeleteApplication(application, {
    onError: () => {
      setIsDeleting(false);
    },
    onSuccess: () => {
      // Artificially slow down the process to make the action feel more secure
      window.setTimeout(() => {
        setIsDeleting(false);
        queryClient.invalidateQueries(['applications']).then(() => {
          // Deleting the workspace we are currently in invalidates the URL
          // context, so leave; otherwise stay on the current page.
          if (application.id === applicationID) {
            navigate('/');
            return;
          }

          onClose();
        });
      }, 400);
    },
  });

  const [confirmation, setConfirmation] = React.useState<string>('');
  const isDisabled = confirmation.trim() !== application.name;

  const [isDeleting, setIsDeleting] = React.useState(false);

  function handleDeleteClick() {
    setIsDeleting(true);
    deleteApplication();
  }

  return (
    <Modal
      opened={opened}
      className={c('delete-app-modal')}
      title="Are you sure you want to delete this workspace?"
      onClose={onClose}
    >
      <p>
        Deleting this workspace will remove and unlink all associated gateways,
        cameras, and streams. All pipelines and deployments will be deleted.
      </p>
      <p>Are you sure you want to delete this workspace?</p>
      <Field
        id="delete-application-name"
        label={
          <>
            Please type <em>{application.name}</em> to confirm.
          </>
        }
      >
        <Input
          id="delete-application-name"
          value={confirmation}
          onValueChange={setConfirmation}
          required
        />
      </Field>

      <ButtonGroup>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          intent="danger"
          onClick={handleDeleteClick}
          disabled={isDisabled}
          loading={isDeleting}
        >
          Permanently delete workspace
        </Button>
      </ButtonGroup>
    </Modal>
  );
}
